import { MutableRefObject, useEffect } from 'react';
import { useFlowBuilder2PageStore } from '../Store/useFlowBuilder2PageStore';

export interface IUseLayoutAcceptRejectObserverProps {
  ref: MutableRefObject<HTMLElement | null>;
  layoutId: string;
}

export const useLayoutAcceptRejectObserver = ({
  ref,
  layoutId,
}: IUseLayoutAcceptRejectObserverProps) => {
  const pendingStatuses = useFlowBuilder2PageStore((s) => s.aiPendingStatuses);
  const addDraftInView = useFlowBuilder2PageStore((s) => s.addDraftInView);
  const removeDraftInView = useFlowBuilder2PageStore((s) => s.removeDraftInView);
  const isScrolling = useFlowBuilder2PageStore((s) => s.isScrolling);

  const { layouts, flows } = pendingStatuses;
  const hasOnlyPendingLayouts = layouts.length > 0 && flows.length === 0;
  const isPendingLayout = layouts.includes(layoutId);

  useEffect(() => {
    if (!hasOnlyPendingLayouts || isScrolling || !isPendingLayout) return;

    const observer = new IntersectionObserver((entries) => {
      if (!hasOnlyPendingLayouts || isScrolling) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          addDraftInView(layoutId);
        } else {
          removeDraftInView(layoutId);
        }
      });
    });

    if (ref.current) observer.observe(ref.current);

    return () => {
      observer.disconnect();
      removeDraftInView(layoutId);
    };
  }, [
    ref,
    layoutId,
    layouts,
    hasOnlyPendingLayouts,
    isScrolling,
    addDraftInView,
    removeDraftInView,
    isPendingLayout,
  ]);
};
